import { useCallback, useEffect, useState } from 'react';
import { getUbicacionClima, setUbicacionClima, type UbicacionClima } from '../services/weather';

/**
 * La ubicación con la que se consulta el clima.
 *
 * Existe porque el clima necesita UN punto y un cliente puede tener campos
 * en varios lados —o ninguno todavía—. Antes de esto no había forma de
 * decirle a la plataforma "el clima que me importa es el de aquí".
 *
 * El orden en que se resuelve importa:
 *
 *  1. Lo que el cliente eligió en Configuración. Vive en el servidor, así
 *     que vale igual desde cualquier computadora y para todo su equipo.
 *  2. Si no eligió nada, el promedio de sus sensores: es una suposición
 *     razonable y evita una pantalla vacía el primer día.
 *  3. Si tampoco hay sensores, null — y la pantalla invita a configurarlo
 *     en vez de inventarse unas coordenadas.
 *
 * El paso 2 es una conveniencia, no un guardado: no se escribe nada en el
 * servidor a espaldas del usuario. Si mueve un sensor, el clima lo sigue;
 * el día que quiera fijarlo, lo fija.
 */
export function useUbicacionClima(sensores?: Array<{ lat: number; lng: number }>) {
  const [configurada, setConfigurada] = useState<UbicacionClima | null>(null);
  const [cargado, setCargado] = useState(false);

  useEffect(() => {
    let vivo = true;
    getUbicacionClima()
      .then(u => { if (vivo) setConfigurada(u); })
      // Un 403 aquí significa "este cliente no tiene el módulo de clima",
      // que es una respuesta legítima y no algo que reportar.
      .catch(() => { if (vivo) setConfigurada(null); })
      .finally(() => { if (vivo) setCargado(true); });
    return () => { vivo = false; };
  }, []);

  const guardar = useCallback(async (u: UbicacionClima) => {
    const guardada = await setUbicacionClima(u);
    setConfigurada(guardada);
    return guardada;
  }, []);

  const validos = (sensores ?? []).filter(
    s => Number.isFinite(s.lat) && Number.isFinite(s.lng) && (s.lat !== 0 || s.lng !== 0),
  );
  const promedio =
    validos.length > 0
      ? {
          lat: validos.reduce((a, s) => a + s.lat, 0) / validos.length,
          lng: validos.reduce((a, s) => a + s.lng, 0) / validos.length,
          nombre: '',
        }
      : null;

  return {
    /** La que se debe usar para pedir el clima. */
    ubicacion: configurada ?? promedio,
    /** Sólo la elegida a mano — para saber si Configuración ya se tocó. */
    configurada,
    esDeSensores: !configurada && promedio !== null,
    cargado,
    guardar,
  };
}
